import type { Todo } from "../../domain/entities.ts";

/** Explicit null detaches a task even when an older parent relationship remains. */
export function parentTaskId(todo: Todo): string | null {
  if (todo.parentTaskId !== undefined) return todo.parentTaskId || null;
  const legacy = (todo.relationships || []).filter(link => link.type === "parent");
  return legacy.length === 1 ? legacy[0].taskId : null;
}

export function hierarchyParentId(tasks: readonly Todo[], todo: Todo): string | null {
  const id = parentTaskId(todo);
  if (!id) return null;
  const parent = tasks.find(task => task.id === id);
  if (!parent) return todo.parentTaskId !== undefined ? id : null;
  if (parent.deleted || parent.rootId !== todo.rootId || parent.categoryId !== todo.categoryId || parentTaskId(parent) || todo.recurrence) return null;
  if (todo.parentTaskId !== undefined) return id;
  const legacySiblings = tasks.filter(task => !task.deleted && task.id !== parent.id && task.rootId === parent.rootId && task.categoryId === parent.categoryId && !task.recurrence && task.parentTaskId === undefined && parentTaskId(task) === id).sort((left, right) => left.id.localeCompare(right.id));
  const index = legacySiblings.findIndex(task => task.id === todo.id);
  return index >= 0 && index < 100 ? id : null;
}

export function childTasks(tasks: readonly Todo[], parent: Todo, includeDeleted = false): Todo[] {
  return tasks.filter(task => (includeDeleted || !task.deleted) && task.id !== parent.id && task.rootId === parent.rootId && task.categoryId === parent.categoryId && (includeDeleted ? parentTaskId(task) : hierarchyParentId(tasks, task)) === parent.id)
    .sort((left, right) => left.position - right.position || left.createdAt - right.createdAt || left.id.localeCompare(right.id));
}

export function validParentCandidates(tasks: readonly Todo[], child: Todo): Todo[] {
  return tasks.filter(task => !task.deleted && task.id !== child.id && task.rootId === child.rootId && task.categoryId === child.categoryId && !parentTaskId(task) && !childTasks(tasks, child).some(item => item.id === task.id) && (parentTaskId(child) === task.id || childTasks(tasks, task).length < 100));
}

/** Expand a selection to include children whenever its parent is selected. */
export function selectedTaskFamilies(tasks: readonly Todo[], selected: readonly Todo[]): Todo[] {
  const ids = new Set(selected.map(task => task.id));
  for (const task of selected) {
    if (!hierarchyParentId(tasks, task)) for (const child of childTasks(tasks, task)) ids.add(child.id);
  }
  return tasks.filter(task => ids.has(task.id));
}
